import React from 'react';
import {
  FavoriteRounded,
  RouteRounded,
  TravelExploreRounded,
  VerifiedUserRounded
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PublicPageLayout from '../components/PublicPageLayout';
import { FONT_FAMILY } from '../common/sharedFunctions';

const accent = '#B7EE00';
const ink = '#111411';

const AboutUs = () => {
  const { t } = useTranslation();
  const values = [
    {
      description: t('about_local_description', {
        defaultValue: 'Designed around the roads, rhythms, and everyday journeys of Shillong.'
      }),
      icon: <TravelExploreRounded />,
      title: t('about_local_title', { defaultValue: 'Local by design' })
    },
    {
      description: t('about_simple_description', {
        defaultValue: 'A clear booking experience that keeps the important details easy to understand.'
      }),
      icon: <RouteRounded />,
      title: t('about_simple_title', { defaultValue: 'Simple from the start' })
    },
    {
      description: t('about_trust_description', {
        defaultValue: 'Thoughtful safety and service details that support a more dependable ride.'
      }),
      icon: <VerifiedUserRounded />,
      title: t('about_trust_title', { defaultValue: 'Built on trust' })
    }
  ];

  return (
    <PublicPageLayout
      eyebrow={t('our_story', { defaultValue: 'Our story' })}
      icon={<FavoriteRounded sx={{ fontSize: 23 }} />}
      subtitle={t('about_page_subtitle', {
        defaultValue: 'A local ride experience shaped around simpler booking, clearer journeys, and the people who move Shillong.'
      })}
      title={t('about_us')}
    >
      <Box sx={{
        alignItems: 'stretch',
        display: 'grid',
        gap: { xs: 3, md: 4 },
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1.15fr 0.85fr' }
      }}>
        <Box sx={{ py: { md: 1 } }}>
          <Typography sx={{
            color: '#697169',
            fontFamily: FONT_FAMILY,
            fontSize: '0.75rem',
            fontWeight: 900,
            letterSpacing: '0.13em',
            mb: 1.5,
            textTransform: 'uppercase'
          }}>
            {t('who_we_are', { defaultValue: 'Who we are' })}
          </Typography>
          <Typography component="h2" sx={{
            color: ink,
            fontFamily: FONT_FAMILY,
            fontSize: { xs: '1.75rem', sm: '2.35rem' },
            fontWeight: 850,
            letterSpacing: '-0.035em',
            lineHeight: 1.08,
            mb: 2.5
          }}>
            {t('about_content_heading', {
              defaultValue: 'Moving local journeys forward, one ride at a time.'
            })}
          </Typography>
          {[t('about_us_content2'), t('about_us_content1')].map((paragraph) => (
            <Typography key={paragraph} sx={{
              color: '#626B62',
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '0.96rem', sm: '1rem' },
              lineHeight: 1.8,
              mb: 2
            }}>
              {paragraph}
            </Typography>
          ))}
        </Box>

        <Box sx={{
          backgroundColor: accent,
          borderRadius: '22px',
          color: ink,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 310,
          overflow: 'hidden',
          p: { xs: 2.75, sm: 3.5 },
          position: 'relative'
        }}>
          <Typography aria-hidden="true" sx={{
            bottom: -36,
            color: 'rgba(17,20,17,0.08)',
            fontFamily: FONT_FAMILY,
            fontSize: '8rem',
            fontWeight: 900,
            lineHeight: 1,
            position: 'absolute',
            right: -8
          }}>
            L
          </Typography>
          <Box sx={{
            alignItems: 'center',
            backgroundColor: ink,
            borderRadius: '14px',
            color: accent,
            display: 'flex',
            height: 50,
            justifyContent: 'center',
            width: 50
          }}>
            <TravelExploreRounded />
          </Box>
          <Box sx={{ position: 'relative' }}>
            <Typography sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '1.65rem', sm: '2rem' },
              fontWeight: 900,
              letterSpacing: '-0.04em',
              lineHeight: 1.05,
              mb: 1.25
            }}>
              {t('rooted_in_shillong', { defaultValue: 'Rooted in Shillong.' })}
            </Typography>
            <Typography sx={{
              color: 'rgba(17,20,17,0.7)',
              fontFamily: FONT_FAMILY,
              fontSize: '0.92rem',
              lineHeight: 1.65,
              maxWidth: 330
            }}>
              {t('rooted_in_shillong_description', {
                defaultValue: 'Built with local movement in mind and focused on making everyday travel feel easier.'
              })}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{
        borderTop: '1px solid #E4E8DF',
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
        mt: { xs: 4, md: 5 },
        pt: { xs: 3, md: 4 }
      }}>
        {values.map((value) => (
          <Box key={value.title} sx={{
            backgroundColor: '#F5F7F1',
            border: '1px solid #E5E9E0',
            borderRadius: '18px',
            p: 2.5
          }}>
            <Box sx={{ color: '#6E9500', display: 'flex', mb: 2 }}>
              {value.icon}
            </Box>
            <Typography sx={{
              color: ink,
              fontFamily: FONT_FAMILY,
              fontSize: '1rem',
              fontWeight: 850,
              mb: 0.8
            }}>
              {value.title}
            </Typography>
            <Typography sx={{
              color: '#697169',
              fontFamily: FONT_FAMILY,
              fontSize: '0.85rem',
              lineHeight: 1.65
            }}>
              {value.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </PublicPageLayout>
  );
};

export default AboutUs;
